import axios, { type InternalAxiosRequestConfig } from 'axios';
import { getRecaptchaToken } from './recaptcha';

declare module 'axios' {
  export interface AxiosRequestConfig {
    recaptchaAction?: string;
  }
}

export const RECAPTCHA_TOKEN_HEADER = 'x-recaptcha-token';

const API_URL = import.meta.env.VITE_API_URL || 'https://dev.api.giotheapp.com';

export const AUTH_RECAPTCHA_ACTIONS: Record<string, string> = {
  '/api/auth/login': 'login',
  '/api/auth/register': 'register',
  '/api/auth/forgot-password': 'forgot_password',
  '/api/auth/reset-password': 'reset_password',
  '/api/auth/change-password': 'change_password',
  '/api/auth/refresh': 'refresh',
  '/api/auth/verify-association-token': 'verify_association_token',
  '/api/auth/request-access': 'request_access',
  '/api/auth/request-account-deletion': 'request_account_deletion',
  '/api/auth/send-email-otp': 'send_email_otp',
  '/api/auth/delete-account': 'delete_account',
  '/api/auth/send-phone-otp': 'send_phone_otp',
  '/api/auth/signin-otp': 'signin_otp',
  '/api/auth/signup-otp': 'signup_otp',
  '/api/auth/notify-admin-access': 'notify_admin_access',
  '/api/auth/resend-email-otp': 'resend_email_otp',
  '/api/auth/resend-phone-otp': 'resend_phone_otp',
  '/api/auth/verify-email-otp': 'verify_email_otp',
  '/api/auth/verify-phone-otp': 'verify_phone_otp',
  '/api/auth/restaurant-signup/send-otp': 'restaurant_signup_send_otp',
  '/api/auth/restaurant-signup/verify': 'restaurant_signup_verify'
};

const resolveRequestPath = (config: InternalAxiosRequestConfig): string => {
  const url = config.url ?? '';

  let path = url;
  if (path.startsWith('http')) {
    path = new URL(path).pathname;
  } else if (!path.startsWith('/') && config.baseURL) {
    const base = config.baseURL.replace(/\/$/, '');
    path = `${base}/${path}`;
    if (path.startsWith('http')) {
      path = new URL(path).pathname;
    }
  }

  const normalized = path.split('?')[0];
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const resolveAuthRecaptchaAction = (
  config: InternalAxiosRequestConfig
): string | undefined => {
  if (config.recaptchaAction) {
    return config.recaptchaAction;
  }

  return AUTH_RECAPTCHA_ACTIONS[resolveRequestPath(config)];
};

export const getRecaptchaActionForAuthUrl = (
  url: string
): string | undefined => {
  const normalized = url.split('?')[0];
  const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return AUTH_RECAPTCHA_ACTIONS[path];
};

export const authApi = axios.create({
  baseURL: API_URL
});

authApi.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const action = resolveAuthRecaptchaAction(config);

  if (!action) {
    return config;
  }

  const token = await getRecaptchaToken(action);
  if (token) {
    config.headers = config.headers ?? {};
    if (typeof config.headers.set === 'function') {
      config.headers.set(RECAPTCHA_TOKEN_HEADER, token);
    } else {
      config.headers[RECAPTCHA_TOKEN_HEADER] = token;
    }
  }

  return config;
});
